import React, { FC } from "react"
import { Body, Button, Container, FlexContainer, H1, Stack } from "@carrotfertility/carotene"
import { IlloWhereAreYouMap } from "../../../views/atoms/Illustrations"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Steps } from "../journey/step-library"
import { useFeatureToggle } from "../../../context/feature-toggle/FeatureToggleContext"
import Settings from "../../../../utils/CarrotConfig"
import { useCurrentUser } from "../../../context/user/UserContext"
import { useRoutingInfo } from "../../context/RoutingInfoContext"
import { FirstResponderType } from "../journey/SetFirstResponderTypeStep"
import { useWindowEvent } from "../../../../utils/hooks/useWindowEvent"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "../../../../reducers/benefitConfiguration"

interface ItsAMatchProps {
  viewName: string
  body: string
  firstResponderType: string
  expertChatLink: string
}

interface FirstResponderChatButtonProps {
  to: string
}

const expertChatLinkMap = {
  CARE_NAVIGATION: Settings.CARROT_PLANS_CARE_NAV_CHAT_URL,
  LGBTQ_FAMILY: Settings.CARROT_PLANS_LGBTQ_FAMILY_CHAT_URL,
  ADOPTION: Settings.CARROT_PLANS_ADOPTION_CHAT_URL,
  GESTATIONAL_CARRIER: Settings.CARROT_PLANS_GESTATIONAL_CARRIER_CHAT_URL,
  FERTILITY: Settings.CARROT_PLANS_FERTILITY_CHAT_URL,
  PREGNANCY: Settings.CARROT_PLANS_PREGNANCY_CHAT_URL
}

const FirstResponderChatButton: FC<FirstResponderChatButtonProps> = ({ to }: FirstResponderChatButtonProps) => {
  const { employeeId } = useCurrentUser()

  const onClick = () => {
    // Adapted from the "Pop-up text" JavaScript snippet Calendly generates for us
    // https://help.calendly.com/hc/en-us/articles/223147027-Embed-options-overview#1
    window.Calendly.initPopupWidget({
      url: to,
      utm: {
        utmCampaign: employeeId,
        utmSource: "CarrotPlansChat"
      }
    })
    return false
  }

  return (
    <div>
      <a onClick={onClick} className="no-underline pointer">
        <Button variant="primary" disabled={false}>
          Schedule an expert chat
        </Button>
      </a>
    </div>
  )
}

function ItsAMatchStep({ viewName, body, firstResponderType, expertChatLink }: ItsAMatchProps): JSX.Element {
  const { setNextStep } = useStateMachine(viewName, null)
  const { featureConfig } = useSelector(getBenefitConfiguration)
  const isFirstResponderChatEnabled = featureConfig?.isFirstResponderChatEnabled

  async function onClickContactMeByEmail(): Promise<void> {
    setNextStep(viewName, "MESSAGE")
  }

  function isCalendlyChatScheduledEvent(e): boolean {
    return e.data.event && e.data.event === "calendly.event_scheduled"
  }

  function handleScheduledCalendlyEvent(e): void {
    if (isCalendlyChatScheduledEvent(e)) {
      setNextStep(viewName, "EXPERT_CHAT")
    }
  }

  useWindowEvent("message", handleScheduledCalendlyEvent)

  return (
    <>
      <FlexContainer justifyContent="center" padding="none" stack="medium">
        <IlloWhereAreYouMap className={null} />
      </FlexContainer>
      <Container>
        <H1 stack="tiny">{"It's a match"}</H1>
        <Container stack="tiny">
          <Body>{body}</Body>
        </Container>
        <FlexContainer paddingTopBottom="huge" justifyContent="center">
          <Stack direction="row" spacing="small">
            {firstResponderType === FirstResponderType.CARE_NAVIGATION && (
              <Button
                variant={isFirstResponderChatEnabled ? "secondary" : "primary"}
                disabled={false}
                onClick={onClickContactMeByEmail}
              >
                Contact me by email
              </Button>
            )}
            {isFirstResponderChatEnabled && <FirstResponderChatButton to={expertChatLink} />}
          </Stack>
        </FlexContainer>
      </Container>
    </>
  )
}

function ItsAMatchUrgentProviderPlacementStep(): JSX.Element {
  return (
    <ItsAMatchStep
      viewName={Steps.ITS_A_MATCH_URGENT_PROVIDER_PLACEMENT}
      body={
        "Based on what you shared, we think that a good next step would be for you to meet with a provider. " +
        "We've matched you with a Carrot Expert who will help you choose one near you."
      }
      firstResponderType={FirstResponderType.CARE_NAVIGATION}
      expertChatLink={Settings.CARROT_PLANS_CARE_NAV_CHAT_URL}
    />
  )
}

function ItsAMatchNonUrgentStep(): JSX.Element {
  const { isLoading, routingData } = useRoutingInfo()
  const firstResponderTypeValue = routingData?.firstResponderType

  return (
    !isLoading && (
      <ItsAMatchStep
        viewName={Steps.ITS_A_MATCH_NON_URGENT}
        body={
          "Based on what you shared, we've matched you with a Carrot Expert who will work with you to develop a " +
          "personalized plan for your journey."
        }
        firstResponderType={firstResponderTypeValue}
        expertChatLink={expertChatLinkMap[firstResponderTypeValue]}
      />
    )
  )
}

export { ItsAMatchUrgentProviderPlacementStep, ItsAMatchNonUrgentStep }
